'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import { useState } from 'react';

import { DIFFICULTY_TIERS, FAMILY, FAMILY_ORDER, SCENARIOS, SHOP_ITEMS } from '../data';
import { DifficultyTier, PrepConfig, ScenarioId, ShopItemId } from '../engine';

import { FamilyAvatar } from './FamilyAvatar';
import { SCENARIO_ICONS } from './icons';

type Props = { onStart: (prep: PrepConfig) => void };

const spentTotal = (purchases: PrepConfig['purchases']): number =>
  Object.entries(purchases).reduce(
    (sum, [itemId, quantity]) => sum + SHOP_ITEMS[itemId as ShopItemId].price * (quantity ?? 0),
    0
  );

export const PrepShop = ({ onStart }: Props) => {
  const [scenarioId, setScenarioId] = useState<ScenarioId>(ScenarioId.LOVELY_SPRING_DAY);
  const [difficultyId, setDifficultyId] = useState<DifficultyTier['id']>('tight');
  const [purchases, setPurchases] = useState<PrepConfig['purchases']>({});

  const tier = DIFFICULTY_TIERS.find(({ id }) => id === difficultyId) ?? DIFFICULTY_TIERS[0];
  const remaining = tier.budget - spentTotal(purchases);

  const setQuantity = (itemId: ShopItemId, quantity: number) => {
    setPurchases((current) => ({ ...current, [itemId]: Math.max(0, quantity) }));
  };

  const handleDifficulty = (id: DifficultyTier['id']) => {
    setDifficultyId(id);
    setPurchases({});
  };

  return (
    <DS.Flex gap={6}>
      <x.section>
        <x.div alignItems="baseline" display="flex" gap={2}>
          <DS.Text as="h2" text="h6">
            Meet your household
          </DS.Text>
          <DS.Text color="uiHelperCopy" text="body12">
            They’ll text. You decide.
          </DS.Text>
        </x.div>
        <x.div display="grid" gap={2} gridTemplateColumns={{ _: '1fr', md: '1fr 1fr 1fr' }} mt={2}>
          {FAMILY_ORDER.map((memberId) => {
            const { bio, name, role } = FAMILY[memberId];
            return (
              <x.div
                alignItems="flex-start"
                bg="uiBgAlternate"
                borderRadius={8}
                display="flex"
                gap={2}
                key={memberId}
                p={3}
              >
                <FamilyAvatar memberId={memberId} sizePx={32} />
                <x.div>
                  <x.b>
                    <span>{`${name} · `}</span>
                    <DS.Text as="span" color="uiHelperCopy" text="body12">
                      {role}
                    </DS.Text>
                  </x.b>
                  <DS.Text color="uiHelperCopy" text="body12">
                    {bio}
                  </DS.Text>
                </x.div>
              </x.div>
            );
          })}
        </x.div>
      </x.section>

      <x.div>
        <DS.Text as="h2" text="h6">
          1. Pick your forecast
        </DS.Text>
        <x.div display="grid" gap={2} gridTemplateColumns={{ _: '1fr', md: '1fr 1fr 1fr' }} mt={2}>
          {Object.values(SCENARIOS).map(({ description, id, name }) => {
            const ScenarioIcon = SCENARIO_ICONS[id];
            return (
              <DS.Card
                density="dense"
                key={id}
                onClick={() => setScenarioId(id)}
                variant={scenarioId === id ? 'primary' : 'secondary'}
              >
                <x.b alignItems="center" display="inline-flex" gap={1}>
                  <ScenarioIcon size={5} />
                  <span>{name}</span>
                </x.b>
                <DS.Text color="uiHelperCopy" text="body12">
                  {description}
                </DS.Text>
                {scenarioId === id && (
                  <x.span alignItems="center" display="inline-flex" gap={1}>
                    <DS.IconCheck size={3} />
                    <DS.Text text="body12">Selected</DS.Text>
                  </x.span>
                )}
              </DS.Card>
            );
          })}
        </x.div>
      </x.div>

      <x.div>
        <x.div alignItems="baseline" display="flex" gap={2}>
          <DS.Text as="h2" text="h6">
            2. Pick your budget
          </DS.Text>
          <DS.Text color="uiHelperCopy" text="body12">
            Blackout Bucks (B$) are game money — not real-world prices.
          </DS.Text>
        </x.div>
        <x.div display="grid" gap={2} gridTemplateColumns={{ _: '1fr', md: '1fr 1fr 1fr' }} mt={2}>
          {DIFFICULTY_TIERS.map(({ budget, id, name }) => (
            <DS.Button
              key={id}
              onClick={() => handleDifficulty(id)}
              pressed={difficultyId === id}
              variant={difficultyId === id ? 'primary' : 'secondary'}
            >
              {`${name} · B$${(budget / 1000).toFixed(0)}k`}
            </DS.Button>
          ))}
        </x.div>
      </x.div>

      <x.div>
        <x.div alignItems="baseline" display="flex" justifyContent="space-between">
          <DS.Text as="h2" text="h6">
            3. Outfit your home
          </DS.Text>
          <x.b color={remaining < 0 ? 'uiError' : 'uiSuccess'}>{`B$${remaining.toLocaleString()} left`}</x.b>
        </x.div>
        <DS.Flex gap={2} mt={2}>
          {Object.values(SHOP_ITEMS).map(({ description, excludes, id, maxQuantity, name, price }) => {
            const quantity = purchases[id] ?? 0;
            const canAfford = remaining >= price;
            const blockedByOther = excludes !== undefined && (purchases[excludes] ?? 0) > 0;
            return (
              <x.div
                alignItems="center"
                bg="uiBgAlternate"
                borderRadius={8}
                display="flex"
                gap={3}
                justifyContent="space-between"
                key={id}
                p={3}
              >
                <x.div>
                  <x.b>{`${name} · B$${price.toLocaleString()}`}</x.b>
                  <DS.Text color="uiHelperCopy" text="body12">
                    {description}
                  </DS.Text>
                </x.div>
                <x.div alignItems="center" display="flex" gap={2}>
                  <DS.Button
                    aria-label={`Remove ${name}`}
                    disabled={quantity === 0}
                    onClick={() => setQuantity(id, quantity - 1)}
                    size="xs"
                    variant="secondary"
                  >
                    −
                  </DS.Button>
                  <x.b color={quantity > 0 ? 'uiHeadingCopy' : 'uiHelperCopy'} minW="16px" textAlign="center">
                    {String(quantity)}
                  </x.b>
                  <DS.Button
                    aria-label={`Add ${name}`}
                    disabled={quantity >= maxQuantity || !canAfford || blockedByOther}
                    onClick={() => setQuantity(id, quantity + 1)}
                    size="xs"
                    variant="secondary"
                  >
                    +
                  </DS.Button>
                </x.div>
              </x.div>
            );
          })}
        </DS.Flex>
      </x.div>

      <DS.Button
        fullWidth
        icon={DS.IconSun}
        iconPosition="right"
        onClick={() => onStart({ difficultyId, purchases, scenarioId })}
        size="md"
        variant="cta"
      >
        Start the day
      </DS.Button>
    </DS.Flex>
  );
};
